import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserService } from 'src/app/services/user.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { IFrameUtils, UrlValidationError } from 'src/app/utilities/iframe.utils';
import { TranslateService } from '@ngx-translate/core';
import { RtcServiceController } from 'src/app/services/rtc.service';
import { BehaviorSubject } from 'rxjs';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { CustomerSupportService } from '../../../services/customer-support.service';
import { DeviceAndBrowserDetectorService } from '../../../services/device-and-browser-detector.service';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { SpaceBoardsService } from '../../../services/space-boards.service';
import { AppsInteractionsLockService } from '../item-wrapper/apps-interactions-lock.service';
import { ItemModel } from '../../../models/itemModel';
import { BoardIframeConfigurations } from '../items-canvas/board-items/board-iframe-configurations';
import { AppIcon, AppIconSource, ItemsCanvasService } from '../../../services/items-canvas.service';
import { BoardItemsInteractionsManager } from '../items-canvas/board-items-interactions-manager';
import { BoardItemsManager } from '../items-canvas/board-items/board-items-manager';
import { FullScreenAppManagerService } from '../services/full-screen-app-manager.service';
import {
  AppConfigurationService,
  CollaborativeApps,
  IAppInformation,
} from './additional-apps.utils';

@UntilDestroy()
@Component({
  selector: 'app-iframe',
  templateUrl: './iframe.component.html',
  styleUrls: ['./iframe.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IframeComponent implements AfterViewInit, OnInit, OnChanges {
  @Input() state!: BoardIframeConfigurations;
  @Input() static!: boolean;

  @Output() stateChanged = new EventEmitter<BoardIframeConfigurations>();
  @Output() deleteItem = new EventEmitter<void>();

  @ViewChild('shareableInput') shareableInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('iframe') iframe: ElementRef | undefined;
  @ViewChild('iframeContainer') iframeContainer: ElementRef | undefined;

  readonly isReloading$ = new BehaviorSubject<boolean>(false);
  readonly showLoadingIndicator$ = new BehaviorSubject<boolean>(true);
  readonly shouldDisableInteractions$ = new BehaviorSubject<boolean>(false);

  readonly ItemModel = ItemModel;
  readonly UrlValidationError = UrlValidationError;
  readonly document = document;

  readonly shareableValueError$ = new BehaviorSubject(UrlValidationError.NoError);

  app?: IAppInformation;
  shareableURLPlaceholder = 'Enter sharable URL';
  shareableValue = '';
  appCreatorIframeUrl?: string;

  isAppCreator = false;

  shouldRunAppOnMobile?: boolean;

  readonly selected$ = new BehaviorSubject<boolean>(false);
  readonly appIcon$ = new BehaviorSubject<AppIcon>({
    type: AppIconSource.ICON,
    content: '',
    height: 0,
    width: 0,
  });

  readonly isFullscreenAppInCall$ = this.fullScreenAppManagerService.isFullScreeningAppInCall$();

  constructor(
    private userService: UserService,
    public spaceRepo: SpaceRepository,
    public spaceBoardsService: SpaceBoardsService,
    private iframeUtils: IFrameUtils,
    private translateService: TranslateService,
    public rtcServiceController: RtcServiceController,
    private changeDetectionRef: ChangeDetectorRef,

    private flagsService: FlagsService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private appsInteractionsLockService: AppsInteractionsLockService,
    private appConfigurationService: AppConfigurationService,
    private itemsCanvasService: ItemsCanvasService,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    private customerSupportService: CustomerSupportService,
    private fullScreenAppManagerService: FullScreenAppManagerService,
  ) {}

  ngOnInit() {
    this.shouldRunAppOnMobile = this.getShouldRunOnMobile();
  }

  async ngAfterViewInit() {
    this.app = this.appConfigurationService.getAppByUid(this.state.appInstanceId);

    const icon = this.app?.icon ?? '';
    this.appIcon$.next({
      type: this.itemsCanvasService.getIconSource(icon),
      content: icon,
      height: 32,
      width: 32,
    });

    if (!this.shouldRunAppOnMobile || this.static) {
      return;
    }

    this.boardItemsInteractionsManager
      .itemSelected$(this.state.uid)
      .pipe(untilDestroyed(this))
      .subscribe((selected) => {
        this.selected$.next(selected);
        this.changeDetectionRef.detectChanges();
      });

    this.appsInteractionsLockService.onInteractionsLockChanged(
      this.state.interactionsLocked,
      this.state.uid,
    );

    this.initAppCreatorIframeUrl();
    this.initUrlPlaceHolder();

    this.setUpInteractionsLock();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const previous = changes.state?.previousValue as BoardIframeConfigurations | undefined;
    const current = changes.state?.currentValue as BoardIframeConfigurations | undefined;

    if (previous && current && previous.interactionsLocked !== current.interactionsLocked) {
      this.appsInteractionsLockService.onInteractionsLockChanged(
        this.state.interactionsLocked,
        this.state.uid,
      );
    }
  }

  private initAppCreatorIframeUrl(): void {
    this.isAppCreator = this.state.ownerId === this.userService.userId;

    if (this.isAppCreator && !this.appCreatorIframeUrl) {
      this.appCreatorIframeUrl = this.state.sharedUrl ?? this.state.url;
    }
  }

  private setUpInteractionsLock() {
    this.appsInteractionsLockService
      .interactionsLockState$(this.state.uid, this.state.interactionsLocked)
      .pipe(untilDestroyed(this))
      .subscribe((interactionsLockState) => {
        // Those are not all the errors, but are all the errors that the user should try to recover from them by themselves
        this.shouldDisableInteractions$.next(interactionsLockState.interactionsDisabledForUser);
        if (this.shouldDisableInteractions$.getValue()) {
          this.iframe?.nativeElement?.blur && this.iframe.nativeElement.blur();
        }
      });
  }

  protected onkeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key === 'a') {
      this.shareableInputRef?.nativeElement?.select?.();
    }
  }

  private initUrlPlaceHolder(): void {
    this.shareableURLPlaceholder = this.translateService.instant(
      this.app?.iframeAppConfig?.shareablePin
        ? `Enter ${this.app?.title} PIN or sharable URL`
        : `Enter sharable ${this.app?.title} URL`,
    );
  }

  async reloadIFrame() {
    this.isReloading$.next(true);
    this.showLoadingIndicator$.next(true);
    modifiedSetTimeout(() => {
      this.isReloading$.next(false);
    }, 50);
    this.hideLoadingIndicator();
  }

  async validateUrl(): Promise<void> {
    const validateData = await this.iframeUtils.validateUrl(this.shareableValue);
    let error = UrlValidationError.CustomError;
    // Validate if URL is valid, else validate if PIN allowed and valid
    if (validateData.error === UrlValidationError.NoError) {
      this.shareableValue = validateData.modifiedUrl;
      this.setSharedURL(this.shareableValue);
      error = UrlValidationError.NoError;
    } else if (
      this.app?.iframeAppConfig?.shareablePin &&
      this.iframeUtils.isValidPinCode(this.shareableValue)
    ) {
      error = UrlValidationError.NoError;
      const sharedUrl = this.app?.iframeAppConfig?.sharedUrl?.replace(
        'PENCIL_PIN_CODE',
        this.shareableValue,
      );
      this.setSharedURL(sharedUrl);
    }

    this.shareableValueError$.next(error);
  }

  setSharedURL(url: string | undefined): void {
    if (!url) {
      return;
    }

    this.state.sharedUrl = url;
    this.emitChanges();

    // To update the template since we are updating an input value
    this.changeDetectionRef.detectChanges();

    this.shareableValue = '';
  }

  hideLoadingIndicator(): void {
    modifiedSetTimeout(() => this.showLoadingIndicator$.next(false), 1000);
  }

  getShouldRunOnMobile(): boolean {
    return !(
      this.deviceAndBrowserDetectorService.isMobile() &&
      this.flagsService.isFlagEnabled(FLAGS.DISABLE_APPS_ON_MOBILE) &&
      !this.isForcedToBeAvailableOnMobile(CollaborativeApps[this.state.appInstanceId])
    );
  }

  isForcedToBeAvailableOnMobile(appId?: CollaborativeApps): boolean {
    if (!appId) {
      return false;
    }
    return this.appConfigurationService.getAppByUid(appId)?.forceAvailableOnMobile || false;
  }

  onInteractionsLockChanges(interactionsLocked: boolean): void {
    this.state.interactionsLocked = interactionsLocked;
    this.appsInteractionsLockService.onInteractionsLockChanged(interactionsLocked, this.state.uid);
    this.emitChanges();
  }
  onPrivacyChanged(isPrivate: boolean): void {
    this.state.isPrivate = isPrivate;
    this.emitChanges();
  }
  onDeleteItem() {
    this.deleteItem.emit();
  }

  emitChanges() {
    this.stateChanged.emit(this.state);
  }

  handleDuplicate() {
    BoardItemsManager.instance?.insertNewBoardItem<ItemModel.IFrame>(
      ItemModel.IFrame,
      {
        appInstanceId: this.state.appInstanceId,
        url: this.state.url,
      },
      {
        renderInNearestFreeSpace: true,
      },
    );
  }

  onOpenLink(link: string) {
    this.customerSupportService.openLink(link);
  }
}
